import { dateFr } from "./format";
import { SID, track } from "./track";
import type { SelectedTask } from "../state/AppState";

/** Nom du paramètre portant l'identifiant du parrain dans un lien partagé. */
export const PARAM_PARRAIN = "p";

/**
 * Le chemin court du parrainage.
 *
 * `**` renvoie déjà tout ce qui n'est pas une fonction vers `index.html` :
 * aucune règle d'hébergement n'est à ajouter, l'application reçoit l'adresse
 * et la lit elle-même.
 */
export const CHEMIN_PARRAIN = "/p/";

/**
 * Lien à partager. Il porte l'identifiant de session de celui qui partage, sans
 * lequel aucun parrainage ne peut être attribué : c'est la seule trace qui relie
 * un diagnostic à la personne qui l'a provoqué (ticket T15).
 *
 * `sid` est tiré au hasard à chaque visite et n'identifie personne — il relie
 * des évènements entre eux, pas une personne à un nom.
 */
export function lienDeParrainage(): string {
  return `${window.location.origin}${CHEMIN_PARRAIN}${SID}`;
}

/**
 * Le lien de parrainage **tel qu'on le recopie depuis une image** — 25/08/2026.
 *
 * La carte d'indice ne portait que l'hôte, et son propre fichier disait
 * pourquoi elle existe : « la croissance de ce produit passe par une image :
 * un lien se lit, une image se montre ». L'objet le plus partageable du
 * produit était donc un cul-de-sac — qui le voyait devait retrouver le
 * domaine de mémoire, et le partage n'était crédité à personne.
 *
 * Une image ne se clique pas : elle se recopie. Le chemin est donc court, sans
 * point d'interrogation ni signe égal, et il tient sur une ligne à côté de
 * l'hôte. `stratera.riseafrica.io/p/ab12cd34` se retape ; `?p=ab12cd34` non.
 */
export function cheminDeParrainage(): string {
  return `${CHEMIN_PARRAIN}${SID}`;
}

/** Identifiant du parrain lu dans l'adresse, s'il y en a un. */
export function parrainDepuisUrl(): string | null {
  /*
    Deux formes, et la seconde ne peut pas disparaître.

    Les liens en `?p=` sont déjà partis — dans des conversations WhatsApp, des
    messages, des signets. Ils continuent de valoir : cesser de les lire
    reviendrait à retirer leurs filleuls aux personnes qui ont partagé les
    premières, c'est-à-dire à punir exactement celles qu'on veut récompenser.
  */
  const chemin = window.location.pathname;
  const brut = chemin.startsWith(CHEMIN_PARRAIN)
    ? chemin.slice(CHEMIN_PARRAIN.length)
    : new URLSearchParams(window.location.search).get(PARAM_PARRAIN);
  if (brut === null || brut === "") return null;
  // Un `sid` fait huit caractères alphanumériques : on n'accepte rien d'autre.
  return /^[a-z0-9]{1,32}$/i.test(brut) ? brut : null;
}

/** Ouvre WhatsApp avec un message pré-rempli. Rien n'est publié sans ce geste. */
export function shareWhatsApp(text: string): void {
  const message = `${text} ${lienDeParrainage()}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
}

/** Copie le lien du test — sans le score. */
export async function copyTestLink(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(
      `Diagnostic d'exposition professionnelle à l'IA — ${lienDeParrainage()}`,
    );
    return true;
  } catch {
    return false;
  }
}

function download(name: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** Télécharge le résultat du volet 1 en texte brut. */
export function downloadResult(params: {
  metier: string;
  score: number;
  gradient: string;
  taches: SelectedTask[];
  premiereAction: readonly [string, string];
}): void {
  const lignes = [
    "DIAGNOSTIC D'EXPOSITION À L'IA — STRATERA",
    "",
    `Métier : ${params.metier}`,
    `Indice : ${params.score}/100 (${params.gradient})`,
    `Date : ${dateFr()}`,
    "",
    "VOS TÂCHES",
    ...params.taches.map(
      (t) =>
        ` - ${t.texte}  [${
          t.niveau === "high" ? "AUTOMATISABLE" : t.niveau === "mid" ? "PARTIEL" : "RÉSISTE"
        }]`,
    ),
    "",
    "PREMIÈRE ACTION",
    `  ${params.premiereAction[0]}`,
    `  ${params.premiereAction[1]}`,
    "",
    // L'adresse n'est pas écrite en dur : elle l'était, et pointait vers un
    // domaine qui n'est pas celui qui sert la page. Celle du navigateur est la
    // seule qu'on sache exacte au moment où le fichier est produit.
    `Méthode : niveaux de référence dérivés de l'indice d'exposition professionnelle à l'IA générative de l'OIT (Gmyrek et al., ILO Working Paper 140, 2025, CC BY 4.0). Adaptation RISEAFRICA, non approuvée par l'OIT, publiée sur ${window.location.host}. Un indice élevé n'est pas une prévision de perte d'emploi.`,
  ];
  download(
    `diagnostic-ia-${new Date().toISOString().slice(0, 10)}.txt`,
    lignes.join("\n"),
    "text/plain;charset=utf-8",
  );
  track("download_result", { score: params.score });
}

/**
 * Le rappel d'agenda — la seule boucle de retour d'un produit qui ne garde rien.
 *
 * Elle mérite d'être nommée pour ce qu'elle est. Le produit ne demande ni nom,
 * ni numéro, ni adresse : il n'a donc **aucun moyen de rappeler qui que ce
 * soit**, et c'est un choix, pas un manque. Le fichier d'agenda est la
 * conséquence honnête de ce choix — c'est la personne qui garde le rappel,
 * chez elle, dans un calendrier que nous ne voyons pas.
 *
 * Il servait sur un écran de résultat sur quatre. Le volet 2, le volet 3 et
 * l'écran dirigeant n'en avaient pas, alors que ce dernier porte le livrable à
 * 15 000 F et la mesure qui bouge le plus vite. La fonction prend donc ce qu'il
 * faut écrire plutôt qu'un indice, parce qu'un dirigeant ne revient pas
 * comparer un indice : il revient comparer des heures.
 *
 * `escapeIcs` n'est pas une précaution de style. Le format sépare ses champs
 * par des virgules et des points-virgules ; un métier écrit « chargé de
 * clientèle, agence » couperait la description en deux et produirait un
 * fichier qu'aucun agenda n'ouvre.
 */
export interface RappelAgenda {
  /** Le titre de l'évènement, tel qu'il apparaîtra dans l'agenda. */
  titre: string;
  /** Ce que la personne doit retrouver dans trois mois : sa mesure d'aujourd'hui. */
  mesure: string;
  /** Le chemin où refaire la mesure, relatif au site. */
  chemin?: string;
  /** Ce qu'on note dans les compteurs — quel écran a produit ce rappel. */
  ecran: string;
}

function escapeIcs(valeur: string): string {
  return valeur.replace(/\\/g, "\\\\").replace(/[;,]/g, (c) => `\\${c}`).replace(/\r?\n/g, "\\n");
}

/**
 * La date du rendez-vous, calculée à un seul endroit — 09/09/2026.
 *
 * Elle l'était deux fois : ici, pour l'évènement déposé dans l'agenda, et dans
 * `RappelAgenda`, qui **l'annonce à la personne avant qu'elle ne dépose le
 * fichier**. Les deux valeurs concordaient, et rien ne les y obligeait. Décaler
 * l'une aurait affiché une date à l'écran et rangé l'évènement à une autre —
 * un écart que personne ne peut constater avant l'échéance, c'est-à-dire trois
 * mois après l'avoir introduit.
 *
 * `maintenant` est paramétrable pour que le test n'ait pas à geler l'horloge.
 */
export function dateDuRappel(maintenant: number = Date.now()): Date {
  return new Date(maintenant + 90 * 86400000);
}

export function downloadReminder(rappel: RappelAgenda): void {
  const date = dateDuRappel();
  const stamp = `${date.toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`;
  const adresse = `${window.location.origin}${rappel.chemin ?? "/"}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//STRATERA//diagnostic//FR",
    "BEGIN:VEVENT",
    `DTSTART:${stamp}`,
    `DTEND:${stamp}`,
    `SUMMARY:${escapeIcs(rappel.titre)}`,
    /*
      La dérive de la mesure est dite ici — 25/08/2026.

      Le produit assume, en interne, qu'« au-delà de la durée de vie d'une
      entrée de cache, une nouvelle génération peut déplacer le score de
      quelques points » : les tâches viennent du modèle, et deux générations ne
      les découpent pas exactement pareil. C'est un arbitrage défendable, et il
      n'était dit nulle part à la personne.

      Or ce rappel-ci lui promet de comparer deux chiffres dans trois mois. Si
      l'écart vient de la mesure et non de son travail, la promesse se retourne
      contre le produit au moment précis où elle devait le prouver. Trois mots
      suffisent à l'éviter, et ils tiennent dans un agenda.

      La phrase ne nomme aucune unité — 08/09/2026. Elle disait « quelques
      points de variation [...] pas la décimale », et ce fichier sert les quatre
      écrans : un indice sur cent n'a pas de décimale à regarder, un équivalent
      temps plein n'a pas de points. La même phrase se trompait donc pour tout
      le monde, dans un document que la personne garde trois mois.
    */
    `DESCRIPTION:${escapeIcs(`${rappel.mesure} Ce qui compte est l'écart, pas la valeur — et une part de cet écart peut venir de la mesure elle-même : regardez le sens, pas le dernier chiffre. ${adresse}`)}`,
    `URL:${adresse}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  download("rappel-90-jours.ics", ics, "text/calendar");
  track("ics_download", { screen: rappel.ecran });
}
